import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { playProceduralSound } from '@/components/System/SoundManager';

// Transicao de entrada pos-autenticacao: comporta de nave/bunker que destrava
// e se abre sobre o destino (~1,4 s). O splash completo toca so na Landing.
// O destino e liberado logo no inicio, para montar atras das portas e ja
// estar pronto quando elas se abrirem.

const RELEASE_AT = 120;
const UNLOCK_AT = 420;
const OPEN_AT = 640;
const DONE_AT = 1300;
const REDUCED_DONE_AT = 450;

const DOOR_EASE = [0.76, 0, 0.24, 1];
const BOLT_ROWS = ['28%', '72%'];

const doorTexture = {
  backgroundColor: '#07090c',
  backgroundImage: [
    'repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 3px)',
    'linear-gradient(rgba(0,224,255,0.07) 1px, transparent 1px)',
    'linear-gradient(90deg, rgba(0,224,255,0.05) 1px, transparent 1px)',
    'linear-gradient(180deg, #11161c 0%, #07090c 55%, #0b0f14 100%)',
  ].join(', '),
  backgroundSize: 'auto, 48px 48px, 48px 48px, auto',
};

function releasePendingEntry(target) {
  if (target?.type === 'route' && target.path) {
    window.history.pushState({}, '', target.path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    return;
  }

  const nextTarget = target?.type || target;

  if (nextTarget === 'stagingRoom') {
    window.__openStagingRoom = true;
    return;
  }

  window.__openRoomSelector = true;
}

function DoorPanel({ side, open }) {
  const isLeft = side === 'left';

  return (
    <motion.div
      className="absolute inset-y-0 w-1/2 overflow-hidden"
      style={{ ...doorTexture, [isLeft ? 'left' : 'right']: 0 }}
      initial={{ x: 0 }}
      animate={{ x: open ? (isLeft ? '-102%' : '102%') : 0 }}
      transition={{ duration: 0.62, ease: DOOR_EASE }}
    >
      {/* Borda de vedacao: traco ciano grosso na junta central. */}
      <div
        className={`absolute inset-y-0 w-[3px] bg-cyan-300/80 shadow-[0_0_14px_rgba(0,224,255,0.55)] ${isLeft ? 'right-0' : 'left-0'}`}
      />
      {/* Placa estrutural com cotovelo: um canto arredondado e traco superior ambar. */}
      <div
        className={`absolute top-[12%] h-[76%] w-[38%] border-t-4 border-amber-500/70 bg-white/[0.015] ${isLeft ? 'right-[9%] rounded-tl-[28px]' : 'left-[9%] rounded-tr-[28px]'}`}
      >
        <div className="absolute inset-x-[14%] top-[18%] h-px bg-cyan-300/15" />
        <div className="absolute inset-x-[14%] bottom-[18%] h-px bg-cyan-300/15" />
      </div>
      {/* Faixa de reforco horizontal. */}
      <div className="absolute inset-x-0 top-1/2 h-10 -translate-y-1/2 border-y border-cyan-300/10 bg-black/40" />
    </motion.div>
  );
}

function Bolt({ top, unlocked }) {
  const color = unlocked ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)]' : 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.45)]';

  return (
    <div className="absolute left-1/2 h-2.5 w-36 -translate-x-1/2 -translate-y-1/2" style={{ top }}>
      <motion.div
        className={`absolute left-0 top-0 h-full w-1/2 origin-left rounded-l-sm ${color}`}
        animate={{ scaleX: unlocked ? 0 : 1 }}
        transition={{ duration: 0.2, ease: 'easeIn' }}
      />
      <motion.div
        className={`absolute right-0 top-0 h-full w-1/2 origin-right rounded-r-sm ${color}`}
        animate={{ scaleX: unlocked ? 0 : 1 }}
        transition={{ duration: 0.2, ease: 'easeIn' }}
      />
    </div>
  );
}

function EntryDoors({ phase }) {
  const unlocked = phase !== 'sealed';
  const open = phase === 'open';

  return (
    <>
      <DoorPanel side="left" open={open} />
      <DoorPanel side="right" open={open} />

      {/* Luz da junta: acende de cima a baixo e se abre em clarao na abertura. */}
      <motion.div
        className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 origin-center bg-cyan-100"
        style={{ boxShadow: '0 0 18px 4px rgba(0,224,255,0.65)' }}
        initial={{ scaleY: 0, opacity: 1 }}
        animate={open ? { scaleY: 1, opacity: 0, scaleX: 40 } : { scaleY: 1, opacity: 1, scaleX: 1 }}
        transition={open ? { duration: 0.45, ease: 'easeOut' } : { duration: 0.38, ease: 'easeOut' }}
      />

      {/* Interferencia de plasma: uma varredura unica e discreta. */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-cyan-300/[0.07] to-transparent"
        initial={{ top: '-15%' }}
        animate={{ top: '110%' }}
        transition={{ duration: 0.6, ease: 'linear' }}
      />

      {!open && BOLT_ROWS.map((top) => <Bolt key={top} top={top} unlocked={unlocked} />)}

      <AnimatePresence>
        {!open && (
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border-t-2 border-cyan-300/70 bg-black/85 px-7 py-4 text-center rounded-tl-2xl"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.18, delay: 0.08 } }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
          >
            <div className="lcars-heading text-[11px] font-bold uppercase tracking-[0.32em] text-amber-300/90">
              REACTA ORION
            </div>
            <div className={`lcars-heading mt-1.5 whitespace-nowrap text-lg font-bold uppercase tracking-[0.2em] transition-colors duration-200 ${unlocked ? 'text-emerald-300' : 'text-cyan-100'}`}>
              {unlocked ? 'Acesso liberado' : 'Acesso autorizado'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function PostAuthSplashGate() {
  const reduceMotion = useReducedMotion();
  const [request, setRequest] = useState(null);
  const [phase, setPhase] = useState('sealed');

  useEffect(() => {
    const timer = setInterval(() => {
      if (!window.__openPostAuthSplash || request) return;
      setPhase('sealed');
      setRequest(window.__openPostAuthSplash);
      window.__openPostAuthSplash = null;
    }, 250);

    return () => clearInterval(timer);
  }, [request]);

  useEffect(() => {
    if (!request) return undefined;

    const timers = [setTimeout(() => releasePendingEntry(request.target), RELEASE_AT)];

    if (reduceMotion) {
      timers.push(setTimeout(() => setRequest(null), REDUCED_DONE_AT));
    } else {
      timers.push(
        setTimeout(() => setPhase('unlocked'), UNLOCK_AT),
        setTimeout(() => {
          playProceduralSound('door_open', { duration: 0.55 });
          setPhase('open');
        }, OPEN_AT),
        setTimeout(() => setRequest(null), DONE_AT),
      );
    }

    return () => timers.forEach(clearTimeout);
  }, [request, reduceMotion]);

  return (
    <AnimatePresence>
      {request && (
        <motion.div
          key="post-auth-entry"
          className="fixed inset-0 z-[240] overflow-hidden"
          role="status"
          aria-live="polite"
          aria-label="Acesso autorizado, abrindo o sistema"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.12 } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
        >
          {reduceMotion ? (
            <div className="flex h-full items-center justify-center bg-black/90">
              <div className="lcars-heading text-lg font-bold uppercase tracking-[0.2em] text-emerald-300">
                Acesso liberado
              </div>
            </div>
          ) : (
            <EntryDoors phase={phase} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
